export interface SystemHealthMetric {
  label: string;
  value: string;
  percentage: number;
  color: string;
  details: string;
}

export const formatSystemHealthData = (systemHealth: any): SystemHealthMetric[] => {
  if (!systemHealth) {
    return [
      { label: 'Server Memory', value: 'N/A', percentage: 0, color: '#8a8886', details: 'No data available' },
      { label: 'CPU Usage', value: 'N/A', percentage: 0, color: '#8a8886', details: 'No data available' },
      { label: 'Disk Space', value: 'N/A', percentage: 0, color: '#8a8886', details: 'No data available' },
      { label: 'Network I/O', value: 'N/A', percentage: 0, color: '#8a8886', details: 'No data available' }
    ];
  }

  const getColorForPercentage = (percentage: number, isInverted = false): string => {
    if (isInverted) {
      // For network where higher is better
      if (percentage > 80) return '#28a745'; // Green
      if (percentage > 40) return '#ffc107'; // Yellow
      return '#a4262c'; // Red
    } else {
      // For CPU, Memory, Disk where lower is better
      if (percentage > 80) return '#a4262c'; // Red
      if (percentage > 60) return '#ffc107'; // Yellow
      return '#28a745'; // Green
    }
  };

  return [
    {
      label: 'Server Memory',
      value: `${systemHealth.memory.percentage.toFixed(1)}%`,
      percentage: systemHealth.memory.percentage,
      color: getColorForPercentage(systemHealth.memory.percentage),
      details: `${systemHealth.memory.used.toFixed(1)}GB / ${systemHealth.memory.total.toFixed(1)}GB`
    },
    {
      label: 'CPU Usage',
      value: `${systemHealth.cpu.percentage.toFixed(1)}%`,
      percentage: systemHealth.cpu.percentage,
      color: getColorForPercentage(systemHealth.cpu.percentage),
      details: `Load: ${systemHealth.cpu.loadAverage[0]?.toFixed(2)} (${systemHealth.cpu.cores} cores)`
    },
    {
      label: 'Disk Space',
      value: `${systemHealth.disk.percentage.toFixed(1)}%`,
      percentage: systemHealth.disk.percentage,
      color: systemHealth.disk.percentage > 80 ? '#a4262c' : systemHealth.disk.percentage > 60 ? '#ffc107' : '#005a9b',
      details: `${systemHealth.disk.used.toFixed(1)}GB / ${systemHealth.disk.total.toFixed(1)}GB`
    },
    {
      label: 'Network I/O',
      value: `${((systemHealth.network.bytesPerSecond || 0) / 1024 / 1024).toFixed(1)} MB/s`,
      percentage: Math.min(((systemHealth.network.bytesPerSecond || 0) / 1024 / 1024 / 100) * 100, 100),
      color: '#28a745',
      details: `${systemHealth.network.connectionsActive} active connections`
    }
  ];
};

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getHealthStatus = (percentage: number): 'healthy' | 'warning' | 'critical' => {
  if (percentage > 80) return 'critical';
  if (percentage > 60) return 'warning';
  return 'healthy';
};

export const getHealthStatusColor = (status: 'healthy' | 'warning' | 'critical'): string => {
  switch (status) {
    case 'healthy': return '#28a745';
    case 'warning': return '#ffc107';
    case 'critical': return '#a4262c';
    default: return '#8a8886';
  }
};
